import {
  BorderOuterOutlined,
  SyncOutlined,
  FilterOutlined,
  SearchOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import {
  Row,
  Col,
  Input,
  Radio,
  Button,
  Space,
  Form,
  Select,
  DatePicker,
  Table,
  notification,
  Tag,
  InputNumber,
} from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import "../Pages/CSS/DonnesBases.css";
import AdvanceSearchModal from "./AdvanceSearchModal";
import EditFournisseur from "./Modal/EditFournisseur";
import { FamilleService } from "../Services/FamilleService";
import { AdherentsService } from "../Services/AdherentsService";
import { handleEncrypt, handleDecrypt, anonymizeIdentifier, anonymizeName } from "../Utils/Utils";
import { useLocation, useNavigate } from "react-router-dom";
const { Option } = Select;
const { RangePicker } = DatePicker;

const Famille = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialTableData = [];
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedRowForEdit, setSelectedRowForEdit] = useState(null);
  const [codesocOptions, setCodesocOptions] = useState([]);
  const [selectedCodesoc, setSelectedCodesoc] = useState(null);
  const [selectedCodesocAdvance, setSelectedCodesocAdvance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState("a");
  const [tableData, setTableData] = useState(initialTableData);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [filteredData, setFilteredData] = useState(initialTableData);
  const [form] = Form.useForm();
  // Filtre appliqué (celui qui filtre réellement le tableau)
  const [dateCreationFilter, setDateCreationFilter] = useState(null); // preset key or 'range'
  const [dateCreationRange, setDateCreationRange] = useState([null, null]); // [dayjs|null, dayjs|null]
  // Filtre en cours de saisie dans le dropdown (appliqué uniquement au clic "Appliquer")
  const [dateCreationDraftFilter, setDateCreationDraftFilter] = useState(null);
  const [dateCreationDraftRange, setDateCreationDraftRange] = useState([null, null]);

  const [rowCount, setRowCount] = useState(0);

  // Filtres pour les nouvelles colonnes
  const [sexeFilter, setSexeFilter] = useState([]);
  const [ageMin, setAgeMin] = useState(null);
  const [ageMax, setAgeMax] = useState(null);
  const [ageMinDraft, setAgeMinDraft] = useState(null);
  const [ageMaxDraft, setAgeMaxDraft] = useState(null);
  const [suspensionBenefFilter, setSuspensionBenefFilter] = useState([]);
  const [codeBenefFilter, setCodeBenefFilter] = useState([]);
  const [createdByFilter, setCreatedByFilter] = useState([]);
  const [codesocFilter, setCodesocFilter] = useState([]);
  const [nomSearchText, setNomSearchText] = useState("");
  const [prenomsSearchText, setPrenomsSearchText] = useState("");
  const [currentPageData, setCurrentPageData] = useState([]);

  const familleService = new FamilleService();
  const adherentsService = new AdherentsService();

  const parseDateTime = (value) => {
    if (!value) return null;
    if (dayjs.isDayjs(value)) return value;
    if (value instanceof Date) return dayjs(value);

    let raw = String(value).trim();
    if (!raw) return null;

    // Enlever le "T" pour normaliser les dates ISO (ex: 2024-01-15T14:30:00 -> 2024-01-15 14:30:00)
    raw = raw.replace("T", " ");

    const parsedDirect = dayjs(raw);
    if (parsedDirect.isValid()) return parsedDirect;

    // Fallback for "DD/MM/YYYY" or "DD/MM/YYYY HH:mm[:ss]"
    const m = raw.match(
      /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (m) {
      const [, dd, mm, yyyy, HH = "00", MI = "00", SS = "00"] = m;
      const isoLike = `${yyyy}-${mm}-${dd} ${HH}:${MI}:${SS}`;
      const parsed = dayjs(isoLike);
      return parsed.isValid() ? parsed : null;
    }

    return null;
  };

  const formatDateTimeDisplay = (value) => {
    if (!value) return "—";

    // D'abord nettoyer la valeur brute (enlever le T)
    const rawCleaned = String(value).trim().replace("T", " ");

    const d = parseDateTime(value);
    if (d && d.isValid()) {
      // Si l'heure est 00:00:00, afficher uniquement la date
      const hasTime = d.hour() !== 0 || d.minute() !== 0 || d.second() !== 0;
      return hasTime ? d.format("DD-MM-YYYY HH:mm") : d.format("DD-MM-YYYY");
    }

    // Valeur brute nettoyée
    return rawCleaned || "—";
  };

  const parseDateCreation = (value) => {
    if (!value) return null;
    if (dayjs.isDayjs(value)) return value;
    if (value instanceof Date) return dayjs(value);

    const raw = String(value).trim();
    if (!raw) return null;

    // ISO / RFC formats are handled by dayjs directly
    const parsedDirect = dayjs(raw);
    if (parsedDirect.isValid()) return parsedDirect;

    // Fallback for common "DD/MM/YYYY" or "DD/MM/YYYY HH:mm:ss" without customParseFormat plugin
    const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
    if (m) {
      const [, dd, mm, yyyy, HH = "00", MI = "00", SS = "00"] = m;
      const isoLike = `${yyyy}-${mm}-${dd}T${HH}:${MI}:${SS}`;
      const parsed = dayjs(isoLike);
      return parsed.isValid() ? parsed : null;
    }

    return null;
  };

  const getWeekRangeMondayStart = (baseDate) => {
    // Monday as first day of week (without relying on global locale)
    const d = baseDate ?? dayjs();
    const monday = d.subtract((d.day() + 6) % 7, "day").startOf("day");
    const sunday = monday.add(6, "day").endOf("day");
    return [monday, sunday];
  };

  const getDateCreationRangeForPreset = (presetKey) => {
    const now = dayjs();
    switch (presetKey) {
      case "today": {
        return [now.startOf("day"), now.endOf("day")];
      }
      case "yesterday": {
        const d = now.subtract(1, "day");
        return [d.startOf("day"), d.endOf("day")];
      }
      case "this_week": {
        return getWeekRangeMondayStart(now);
      }
      case "last_week": {
        const [monday] = getWeekRangeMondayStart(now);
        const start = monday.subtract(7, "day").startOf("day");
        const end = start.add(6, "day").endOf("day");
        return [start, end];
      }
      case "this_month": {
        return [now.startOf("month"), now.endOf("month")];
      }
      case "last_month": {
        const d = now.subtract(1, "month");
        return [d.startOf("month"), d.endOf("month")];
      }
      case "this_year": {
        return [now.startOf("year"), now.endOf("year")];
      }
      case "last_year": {
        const d = now.subtract(1, "year");
        return [d.startOf("year"), d.endOf("year")];
      }
      default:
        return [null, null];
    }
  };

  const buildDateCreationFilterKey = (filterKey, range) => {
    if (!filterKey) return null;
    if (filterKey !== "range") return filterKey;
    const start = range?.[0];
    const end = range?.[1];
    if (!start || !end) return null;
    return `range:${start.format("YYYY-MM-DD")}:${end.format("YYYY-MM-DD")}`;
  };

  const isDateInRangeInclusive = (recordDate, start, end) => {
    return (
      (recordDate.isAfter(start) || recordDate.isSame(start)) &&
      (recordDate.isBefore(end) || recordDate.isSame(end))
    );
  };

  const matchDateCreationByFilterValue = (filterValue, recordDate) => {
    const key = String(filterValue || "");

    if (key.startsWith("range:")) {
      const [, startStr, endStr] = key.split(":");
      if (!startStr || !endStr) return true;
      const start = dayjs(startStr).startOf("day");
      const end = dayjs(endStr).endOf("day");
      if (!start.isValid() || !end.isValid()) return false;
      return isDateInRangeInclusive(recordDate, start, end);
    }

    const [start, end] = getDateCreationRangeForPreset(key);
    if (!start || !end) return true;
    return isDateInRangeInclusive(recordDate, start, end);
  };

  useEffect(() => {
    const base = Array.isArray(filteredData) ? filteredData : [];
    const appliedKey = buildDateCreationFilterKey(dateCreationFilter, dateCreationRange);

    if (!appliedKey) {
      setRowCount(base.length);
      return;
    }

    const count = base.filter((row) => {
      const d = parseDateCreation(row?.datecreation);
      if (!d) return false;
      return matchDateCreationByFilterValue(appliedKey, d);
    }).length;

    setRowCount(count);
  }, [filteredData, dateCreationFilter, dateCreationRange]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const familleData = await familleService.getAllFamille();
        const participantData = await adherentsService.getAllAdherents();
        console.log("Famille Data:", familleData);
        console.log("Participant Data:", participantData);

        setTableData(familleData);


        const options = [...new Set(participantData.map((participant) => participant.codeadht))]
          .map((codeadht) => {
            const participant = participantData.find((participant) => participant.codeadht === codeadht);
            return {
              codesoc: codeadht,
              societe: participant ? participant.intitule : 'Unknown',
            };
          });

        console.log("Codesoc Options:", options);
        setCodesocOptions(options);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCodesocChange = (value) => {
    setSelectedCodesoc(value);
    if (value === null) {
      setFilteredData(tableData);
    } else {
      setFilteredData(tableData.filter(participant => participant.codesoc === value));
    }
  };

  const handleSearch = (value) => {
    setFilteredData(tableData.filter(item =>
      Object.values(item).some(val => val && val.toString().toLowerCase().includes(value.toLowerCase()))
    ));
  };

  useEffect(() => {
    if (location.state && location.state.selectedOption) {
      setSelectedOption(location.state.selectedOption);
    }
  }, [location.state]);

  const handleResetAll = async () => {
    setLoading(true);
    try {
      const updatedData = await familleService.getAllFamille();
      setTableData(updatedData);
      setSelectedRowForEdit(null);

      // Réinitialiser tous les filtres et recherches
      setDateCreationFilter(null);
      setDateCreationRange([null, null]);
      setDateCreationDraftFilter(null);
      setDateCreationDraftRange([null, null]);
      setSexeFilter([]);
      setAgeMin(null);
      setAgeMax(null);
      setAgeMinDraft(null);
      setAgeMaxDraft(null);
      setSuspensionBenefFilter([]);
      setCodeBenefFilter([]);
      setCreatedByFilter([]);
      setCodesocFilter([]);
      setNomSearchText("");
      setPrenomsSearchText("");
      setCurrentPageData([]);

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      if (selectedRowForEdit) {
        await familleService.updateListFournis(selectedRowForEdit);
        const updatedData = await familleService.getAllFamille();
        setTableData(updatedData);
        setSelectedRowForEdit(null);
        notification.success({
          message: "Modification réussie",
          description: "La prestation a été modifié avec succès.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Erreur",
        description: "Erreur lors de la suppression de la prestation",
      });
    }
  };

  const prepareFamillesDataForExport = (filteredData) => {
    return filteredData.map(famille => {
      const { matribenef, ...rest } = famille; // Exclude 'matricule'
      return {
        ...rest,
        matripart: anonymizeIdentifier(famille.matripart),
        nom: anonymizeName(famille.nom),
        prenoms: anonymizeName(famille.prenom),
      };
    });
  };

  const exportFamillesToExcel = (filteredData) => {
    // const anonymizedData = prepareFamillesDataForExport(filteredData);
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Familles');
    XLSX.writeFile(workbook, 'Familles.xlsx');
  };

  const getDataForExport = () => {
    const base = Array.isArray(filteredData) ? filteredData : [];
    const appliedKey = buildDateCreationFilterKey(dateCreationFilter, dateCreationRange);

    if (!appliedKey) return base;

    return base.filter((row) => {
      const d = parseDateCreation(row?.datecreation);
      if (!d) return false;
      return matchDateCreationByFilterValue(appliedKey, d);
    });
  };

  const exportToExcel = () => {
    // Exporter uniquement les données réellement filtrées et affichées
    const dataToExport = currentPageData.length > 0 ? currentPageData : filteredData;
    if (!dataToExport.length) {
      notification.info({
        message: "Export",
        description: "Aucune donnée à exporter pour les filtres en cours.",
      });
      return;
    }
    exportFamillesToExcel(dataToExport);
  };


  const columns = [
    {
      title: "Matri.Benef",
      dataIndex: "matribenef",
      key: "matribenef",
      render: (text) => <strong>{text}</strong>,
      align: "center",
      sorter: (a, b) => a.matribenef.localeCompare(b.matribenef),
    },
    {
      title: (
        <div
          style={{
            backgroundColor: "#010f1c",
            color: "white",
            paddingLeft: 5,
          }}
        >
          Type Benef
        </div>
      ),

      dataIndex: "codebenef",
      key: "codebenef",
      filters: [
        { text: "Epouse", value: "2" },
        { text: "Enfant", value: "3" },
        { text: "Epoux", value: "4" },
        { text: "Ascendant", value: "5" },
        { text: "Autre", value: "6" },
      ],
      filteredValue: codeBenefFilter,
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) => record.codebenef === value,
      render: (text) => {
        const typeBenefLabels = {
          "2": "Epouse",
          "3": "Enfant",
          "4": "Epoux",
          "5": "Ascendant",
          "6": "Autre",
        };
        return <span style={{ whiteSpace: "nowrap", textAlign: "left", paddingLeft: 5, paddingRight: 5 }}>{typeBenefLabels[text] || "Inconnu"}</span>;
      },
    },
    {
      title: (
        <div
          style={{
            backgroundColor: "#010f1c",
            color: "white",
            paddingLeft: 5,
          }}
        >
          Prénom
        </div>
      ),
      dataIndex: "prenoms",
      key: "prenoms",
      filteredValue: prenomsSearchText ? [prenomsSearchText] : null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Rechercher prénom"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              setPrenomsSearchText(selectedKeys[0] || "");
              confirm();
            }}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                setPrenomsSearchText(selectedKeys[0] || "");
                confirm();
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => {
                setPrenomsSearchText("");
                clearFilters();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record.prenoms
          ? record.prenoms.toString().toLowerCase().includes(value.toLowerCase())
          : "",
      render: (text) => <div style={{ whiteSpace: "nowrap", textAlign: "left", paddingLeft: 5, paddingRight: 5 }}>{text}</div>,
    },
    {
      title: (
        <div
          style={{
            backgroundColor: "#010f1c",
            color: "white",
            paddingLeft: 5,
          }}
        >
          Nom
        </div>
      ),

      dataIndex: "nom",
      key: "nom",
      filteredValue: nomSearchText ? [nomSearchText] : null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Rechercher nom"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              setNomSearchText(selectedKeys[0] || "");
              confirm();
            }}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                setNomSearchText(selectedKeys[0] || "");
                confirm();
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => {
                setNomSearchText("");
                clearFilters();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record.nom
          ? record.nom.toString().toLowerCase().includes(value.toLowerCase())
          : "",
      render: (text) => <div style={{ whiteSpace: "nowrap", textAlign: "left", paddingLeft: 5, paddingRight: 5 }}>{text}</div>,
    },
    {
      title: "Matri.Part",
      dataIndex: "matripart",
      key: "matripart",
      align: "center",
      sorter: (a, b) => a.matripart.localeCompare(b.matripart),
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span>{text}</span>
          <ArrowRightOutlined
            style={{
              cursor: "pointer",
              color: "#1890ff",
              fontSize: 12,
              transition: "color 0.2s",
            }}
            onClick={() => {
              navigate(`/participants/edit/${record.matripart}`);
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#40a9ff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "#1890ff";
            }}
          />
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            backgroundColor: "#010f1c",
            color: "white",
            paddingLeft: 5,
          }}
        >
          Participant
        </div>
      ),

      dataIndex: "prenomPart",
      key: "prenomPart",
      render: (text) => <div style={{ whiteSpace: "nowrap", textAlign: "left", paddingRight: 5, paddingLeft: 5 }}>{text}</div>,
    },
    {
      title: "Code.Soc",
      dataIndex: "codesoc",
      key: "codesoc",
      align: "center",
      sorter: (a, b) => a.codesoc.localeCompare(b.codesoc),
      filters: (() => {
        const baseData = (currentPageData && currentPageData.length > 0) ? currentPageData : filteredData;
        const uniqueCodesoc = [...new Set((baseData || []).map(item => item.codesoc).filter(Boolean))];
        return uniqueCodesoc.map(code => ({
          text: code,
          value: code,
        }));
      })(),
      filteredValue: codesocFilter,
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) => record.codesoc === value,
    },
    {
      title: (
        <div
          style={{
            backgroundColor: "#010f1c",
            color: "white",
            paddingLeft: 5,
          }}
        >
          Adresse
        </div>
      ),

      dataIndex: "adresse",
      key: "adresse",
      render: (text) => <div style={{ whiteSpace: "nowrap", textAlign: "left", paddingRight: 5, paddingLeft: 5 }}>{text}</div>,
    },
    {
      title: "D.naiss",
      dataIndex: "datenaiss",
      key: "datenaiss",
      align: "center",
      render: (text) => dayjs(text).format("DD/MM/YYYY"),
    },
    {
      title: "Lieu.Naissance",
      dataIndex: "lieunaiss",
      key: "lieunaiss",
    },
    {
      title: "Age",
      dataIndex: "agebenef",
      key: "agebenef",
      align: "center",
      sorter: (a, b) => {
        const ageA = a.agebenef !== null && a.agebenef !== undefined ? parseInt(a.agebenef) : -Infinity;
        const ageB = b.agebenef !== null && b.agebenef !== undefined ? parseInt(b.agebenef) : -Infinity;
        return ageA - ageB;
      },
      filteredValue: ageMin !== null || ageMax !== null ? ["hasFilter"] : null,
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilterDropdownOpenChange: (visible) => {
        if (!visible) {
          setAgeMinDraft(ageMin);
          setAgeMaxDraft(ageMax);
        }
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 12, width: 280 }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Filtrer par âge</div>
            <Space>
              <InputNumber
                placeholder="Âge min"
                value={ageMinDraft}
                onChange={(val) => setAgeMinDraft(val)}
                style={{ width: 120 }}
                min={0}
              />
              <InputNumber
                placeholder="Âge max"
                value={ageMaxDraft}
                onChange={(val) => setAgeMaxDraft(val)}
                style={{ width: 120 }}
                min={0}
              />
            </Space>
            <Space style={{ marginTop: 8 }}>
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  setAgeMin(ageMinDraft);
                  setAgeMax(ageMaxDraft);
                  confirm();
                }}
                style={{ width: 100 }}
              >
                Appliquer
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setAgeMin(null);
                  setAgeMax(null);
                  setAgeMinDraft(null);
                  setAgeMaxDraft(null);
                  clearFilters();
                }}
                style={{ width: 100 }}
              >
                Réinitialiser
              </Button>
            </Space>
          </Space>
        </div>
      ),
      onFilter: (value, record) => {
        const age = parseInt(record.agebenef) || 0;
        const min = ageMin !== null ? ageMin : 0;
        const max = ageMax !== null ? ageMax : Infinity;
        return age >= min && age <= max;
      },
    },
    {
      title: "Sexe",
      dataIndex: "sexe",
      key: "sexe",
      align: "center",
      sorter: (a, b) => a.sexe.localeCompare(b.sexe),
      filters: [
        { text: "Masculin", value: "MA" },
        { text: "Féminin", value: "FE" },
      ],
      filteredValue: sexeFilter,
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) => record.sexe === value,
    },
    {
      title: "Etat",
      dataIndex: "suspension_benef",
      key: "suspension_benef",
      fixed: "right",
      filters: [
        { text: "En cours", value: "W" },
        { text: "Actif", value: "A" },
        { text: "Suspendu", value: "S" },
        { text: "Résilié", value: "R" },
      ],
      filteredValue: suspensionBenefFilter,
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) => record.suspension_benef === value,
      render: (text) => {
        const tagColors = {
          "A": "#46de04",
          "S": "#fa0202",
          "R": "#fa0202",
          "W": "#f2b305",
        };
        const tagTexts = {
          "A": "Actif",
          "S": "Suspendu",
          "R": "Résilié",
          "W": "En cours",
        };
        return <Tag color={tagColors[text] || "gray"}>{tagTexts[text] || "Inconnu"}</Tag>;
      },
    },

    //datecreation
    {
      title: "Date création",
      dataIndex: "datecreation",
      key: "datecreation",
      sorter: (a, b) => a.datecreation.localeCompare(b.datecreation),
      filteredValue: (() => {
        const key = buildDateCreationFilterKey(dateCreationFilter, dateCreationRange);
        return key ? [key] : null;
      })(),
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? "#40a9ff" : "#ffffff" }} />
      ),
      onFilterDropdownOpenChange: (open) => {
        if (open) {
          setDateCreationDraftFilter(dateCreationFilter);
          setDateCreationDraftRange([dateCreationRange?.[0] ?? null, dateCreationRange?.[1] ?? null]);
        }
      },
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => {
        const isRange = dateCreationDraftFilter === "range";
        const hasValidRange = Boolean(dateCreationDraftRange?.[0] && dateCreationDraftRange?.[1]);

        const apply = () => {
          setDateCreationFilter(dateCreationDraftFilter ?? null);
          setDateCreationRange([dateCreationDraftRange?.[0] ?? null, dateCreationDraftRange?.[1] ?? null]);

          const key = buildDateCreationFilterKey(dateCreationDraftFilter, dateCreationDraftRange);
          setSelectedKeys(key ? [key] : []);
          confirm();
        };

        const reset = () => {
          setDateCreationFilter(null);
          setDateCreationRange([null, null]);
          setDateCreationDraftFilter(null);
          setDateCreationDraftRange([null, null]);
          setSelectedKeys([]);
          clearFilters?.();
          confirm({ closeDropdown: true });
        };

        return (
          <div style={{ padding: 8, width: 280 }}>
            <Select
              value={dateCreationDraftFilter ?? undefined}
              onChange={(value) => {
                setDateCreationDraftFilter(value ?? null);
                if (value && value !== "range") {
                  const nextRange = getDateCreationRangeForPreset(value);
                  setDateCreationDraftRange(nextRange);
                  setSelectedKeys([buildDateCreationFilterKey(value, nextRange)]);
                } else if (!value) {
                  setDateCreationDraftRange([null, null]);
                  setSelectedKeys([]);
                } else {
                  setSelectedKeys([buildDateCreationFilterKey("range", dateCreationDraftRange) ?? "range:"]);
                }
              }}
              allowClear
              placeholder="Choisir une période"
              style={{ width: "100%" }}
              options={[
                { value: "today", label: "Aujourd’hui" },
                { value: "yesterday", label: "Hier" },
                { value: "this_week", label: "Cette semaine" },
                { value: "last_week", label: "Semaine passée" },
                { value: "this_month", label: "Ce mois-ci" },
                { value: "last_month", label: "Mois dernier" },
                { value: "this_year", label: "Cette année" },
                { value: "last_year", label: "Année passée" },
                { value: "range", label: "Date à date" },
              ]}
            />

            {isRange && (
              <RangePicker
                value={dateCreationDraftRange}
                onChange={(vals) => {
                  const next = vals ?? [null, null];
                  setDateCreationDraftRange(next);
                  setSelectedKeys([buildDateCreationFilterKey("range", next) ?? "range:"]);
                }}
                style={{ width: "100%", marginTop: 8 }}
                allowClear
                format="DD/MM/YYYY"
              />
            )}

            <Space style={{ marginTop: 8 }}>
              <Button
                type="primary"
                size="small"
                onClick={apply}
                disabled={!dateCreationDraftFilter || (isRange && !hasValidRange)}
              >
                Appliquer
              </Button>
              <Button size="small" onClick={reset}>
                Réinitialiser
              </Button>
            </Space>
          </div>
        );
      },
      onFilter: (value, record) => {
        const recordDate = parseDateCreation(record?.datecreation);
        if (!recordDate) return false;
        return matchDateCreationByFilterValue(value, recordDate);
      },
      render: (text) => <div style={{ whiteSpace: "nowrap", textAlign: "left", paddingRight: 5, paddingLeft: 5 }}>{formatDateTimeDisplay(text)}</div>,
    },
    //created_by
    {
      title: "Créé par",
      dataIndex: "created_by",
      key: "created_by",
      sorter: (a, b) => ((a?.created_by ?? "").toString()).localeCompare((b?.created_by ?? "").toString(), "fr", { sensitivity: "base" }),
      filters: (() => {
        const baseData = (currentPageData && currentPageData.length > 0) ? currentPageData : filteredData;
        const uniqueCreators = [...new Set((baseData || []).map(item => item.created_by).filter(Boolean))];
        return uniqueCreators.map(creator => ({
          text: creator,
          value: creator,
        }));
      })(),
      filteredValue: createdByFilter,
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) => record.created_by === value,
      render: (text) => (
        <div style={{ whiteSpace: "nowrap", textAlign: "left", paddingRight: 5, paddingLeft: 5 }}>
          {formatDateTimeDisplay(text)}
        </div>
      ),
    },
  ];

  const handleRadioChange = (e) => {
    setSelectedOption(e.target.value);
  };

  useEffect(() => {
    switch (selectedOption) {
      case "a":
        setFilteredData(tableData);
        break;
      case "b":
        setFilteredData(tableData.filter(item => item.codebenef.trim() === "2"));
        break;
      case "c":
        setFilteredData(tableData.filter(item => item.codebenef.trim() === "3"));
        break;
      case "d":
        setFilteredData(tableData.filter(item => item.codebenef.trim() === "4"));
        break;
      case "e":
        setFilteredData(tableData.filter(item => item.codebenef.trim() === "5"));
        break;
      case "f":
        setFilteredData(tableData.filter(item => item.codebenef.trim() === "6"));
        break;
      case "g":
        setFilteredData(tableData.filter(item => item.codebenef.trim() === "3" && item.agebenef >= 22));
        break;
      default:
        setFilteredData(tableData);
    }
  }, [selectedOption, tableData]);

  const calculateCountByType = (type) => {
    return filteredData.filter((participant) => participant.codebenef === type).length;
  };

  const calculateAdultChildren = () => {
    return filteredData.filter((participant) => participant.codebenef === "3" && participant.agebenef >= 22).length;
  };

  // Mettre à jour le compteur de lignes quand les données filtrées changent
  useEffect(() => {
    // Utiliser currentPageData si disponible (après filtrage de la table), sinon filteredData
    const dataCount = currentPageData.length > 0 ? currentPageData.length : filteredData.length;
    setRowCount(dataCount);
  }, [filteredData, currentPageData]);


  const handleAdvancedSearch = async (values) => {
    const [minAge, maxAge] = values.agebenef
      ? values.agebenef.split('-').map((age) => parseInt(age.trim(), 10))
      : [null, null];

    const payload = {
      codesoc: values.codesoc || "",
      nom: values.nom || null,
      prenoms: values.prenoms || null,
      sexe: values.sexe || null,
      matricule: values.matricule || null,
      age_search: values.agebenef ? values.agebenef : null,
      codebenef: values.codebenef || 'A',
    };

    try {
      const data = await familleService.searchBeneciaire(payload);
      setFilteredData(data);
      setShowAdvancedSearch(false);
    } catch (error) {
      console.error('Erreur lors de la recherche avancée :', error);
    }
  };




  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12} justify="start">
          {/* <Row gutter={[16, 16]}>
            <Col xs={24} sm={6} lg={8}> */}
          <label>
            Épouses : <strong>{calculateCountByType("2")}</strong>&nbsp; Enfants :{" "}
            <strong>{calculateCountByType("3")}</strong> <br />
            Epoux : <strong>{calculateCountByType("4")}</strong>&nbsp; Ascendants :{" "}
            <strong>{calculateCountByType("5")}</strong> <br />
            Autres : <strong>{calculateCountByType("6")}</strong>&nbsp; Enfants majeurs :{" "}
            <strong>{calculateAdultChildren()}</strong>
          </label>
          <Row
            gutter={[16, 16]}
            style={{
              display: "flex",
              textAlign: "start",
              padding: "8px",
              justifyContent: "flex-start",
            }}

          >
            <Space>
              <Col xs={12} sm={6} lg={8}>

                <Input
                  placeholder="Recherche..."
                  bordered={false}
                  style={{ borderBottom: "2px solid #030303", width: "300px" }}
                  onChange={(e) => handleSearch(e.target.value)}
                  onPressEnter={(e) => handleSearch(e.target.value)}
                  prefix={
                    <Space>
                      <BorderOuterOutlined
                        style={{ fontSize: 16, color: "#010f1c" }}
                        onClick={() => setShowAdvancedSearch(true)}
                      />
                    </Space>
                  }
                />
                <AdvanceSearchModal
                  visible={showAdvancedSearch}
                  onCancel={() => setShowAdvancedSearch(false)}
                  onSearch={handleAdvancedSearch}
                  societeOptions={codesocOptions}
                  selectedCodesoc={selectedCodesocAdvance}
                />
              </Col>
              <Col xs={12} sm={6} lg={8}>
                {codesocOptions.length > 0 ? (
                  <Select
                    showSearch
                    bordered={false}
                    style={{ borderBottom: "2px solid #030303", width: "300px" }}
                    onChange={handleCodesocChange}
                    value={selectedCodesoc}
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                      0
                    }
                  >
                    <Select.Option value={null}>Toutes les sociétés</Select.Option>
                    {codesocOptions.map((item) => (
                      <Select.Option key={item.codesoc} value={item.codesoc}>
                        {`${item.codesoc} - ${item.societe}`}
                      </Select.Option>
                    ))}
                  </Select>
                ) : (
                  <span>Loading...</span>
                )}
              </Col>
            </Space>
          </Row>
        </Col>
        <Col xs={24} xl={12}
          justify="end">
          <Row>
            <Col
              span={24}
              style={{
                display: "flex",
                textAlign: "end",
                padding: "8px",
                justifyContent: "flex-end",
              }}>
              <Space>

                <Button
                  onClick={exportToExcel}
                  size="small"
                  className="buttonExport"
                >
                  Exporter
                </Button>
                <Button
                  onClick={handleResetAll}
                  icon={<SyncOutlined />}
                  size="small"
                  // style={{ backgroundColor: "#67f02e", color: "white" }}
                  className="buttonRefresh"
                >
                  Actualiser
                </Button>
              </Space>
            </Col>
          </Row>
          <Row>
            <Col
              span={24}
              style={{
                display: "flex",
                textAlign: "end",
                padding: "8px",
                justifyContent: "flex-end",
              }}>
              <Radio.Group
                value={selectedOption}
                onChange={handleRadioChange}
                style={{ marginBottom: "1rem" }}
              >
                <Radio.Button value="a">Tout</Radio.Button>
                <Radio.Button value="b">Epouses</Radio.Button>
                <Radio.Button value="c">Enfants</Radio.Button>
                <Radio.Button value="d">Epoux</Radio.Button>
                <Radio.Button value="e">Ascendants</Radio.Button>
                <Radio.Button value="f">Autres</Radio.Button>
                <Radio.Button value="g">Enf. Majeur</Radio.Button>
              </Radio.Group>
            </Col>
          </Row>

        </Col>
      </Row>
      {editModalVisible && selectedRowForEdit && (
        <EditFournisseur
          fournisseurDetails={selectedRowForEdit}
          onUpdate={handleUpdate}
          onClose={() => setEditModalVisible(false)}
        />
      )}
      <Table
        scroll={{
          x: true,
        }}
        style={{ cursor: "pointer", whiteSpace: "nowrap", width: "100%" }}
        className="custom-header1-table"
        bordered
        columns={columns}
        dataSource={filteredData}
        pagination={{
          position: ["bottomRight"],
          showSizeChanger: true,
          showTotal: (total, range) => (
            <div style={{ textAlign: "left", fontWeight: 500 }}>
              Enregistrements : <strong>{total}</strong>
            </div>
          ),
        }}
        showSorterTooltip={false}
        loading={loading}
        onChange={(pagination, filters, sorter, { currentDataSource }) => {
          // Stocker les données actuellement affichées après filtrage
          setCurrentPageData(currentDataSource);

          // Synchroniser les états des filtres
          if (filters.sexe !== undefined) {
            setSexeFilter(filters.sexe || []);
          }
          if (filters.suspension_benef !== undefined) {
            setSuspensionBenefFilter(filters.suspension_benef || []);
          }
          if (filters.codebenef !== undefined) {
            setCodeBenefFilter(filters.codebenef || []);
          }
          if (filters.created_by !== undefined) {
            setCreatedByFilter(filters.created_by || []);
          }
          if (filters.codesoc !== undefined) {
            setCodesocFilter(filters.codesoc || []);
          }
        }}
      />

    </div>
  );
};

export default Famille;
